import {
  TRANSITION_ENQUIRE,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_ACCEPT_CUSTOM_PRO_OFFER,
  TRANSITION_JOB_DONE,
  TRANSITION_JOB_DONE_CUSTOMER,
  TRANSITION_JOB_DONE_PRO,
  TRANSITION_JOB_DONE_RELEASE_FUNDS,
  TRANSITION_RELEASE_FUNDS,
  TRANSITION_JOB_ACCEPTED,
  TRANSITION_REVIEW_1_BY_CUSTOMER,
  TRANSITION_REVIEW_2_BY_CUSTOMER,
  TRANSITION_REVIEW_1_BY_PROVIDER,
  TRANSITION_REVIEW_2_BY_PROVIDER,
  TRANSITION_SKIP_TO_REVIEWS,
  TRANSITION_REQUEST_PAYMENT,
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  TRANSITION_MAKE_CUSTOM_OFFER,
  TRANSITION_MAKE_CUSTOM_PRO_OFFER,
  TRANSITION_CANCEL_PAYMENT,
  TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
  TRANSITION_JOB_DONE_INVOICE,
  TRANSITION_ACCEPT_JOB_INVOICE,
  TRANSITION_JOB_DONE_ACCEPT_INVOICE,
} from './transaction';

/**
 * Process step configurations for different transaction processes
 */

const DEFAULT_PROCESS_STEPS = [
  {
    id: 'conversation-started',
    title: 'TransactionSteps.conversationStarted',
    description: 'TransactionSteps.conversationStartedDesc',
    completedBy: [
      TRANSITION_REQUEST_PAYMENT,
      TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
      TRANSITION_MAKE_CUSTOM_OFFER,
    ],
    instruction: {
      customer: 'TransactionSteps.customerDiscussDetails',
      provider: 'TransactionSteps.providerDiscussDetails',
    },
    helpText: 'TransactionSteps.conversationStartedHelp',
  },
  {
    id: 'offer-sent',
    title: 'TransactionSteps.offerSentWaitingAcceptance',
    description: 'TransactionSteps.offerSentDesc',
    completedBy: [TRANSITION_CONFIRM_PAYMENT],
    instruction: {
      customer: 'TransactionSteps.customerReviewOffer',
      provider: 'TransactionSteps.providerWaitingAcceptance',
    },
    action: {
      customer: {
        label: 'ProcessSteps.viewFullDetails',
        handler: 'onOpenOfferModal',
        variant: 'secondary',
      },
    },
    helpText: 'TransactionSteps.offerSentHelp',
  },
  {
    id: 'work-in-progress',
    title: 'TransactionSteps.workInProgress',
    description: 'TransactionSteps.workInProgressDesc',
    completedBy: [
      TRANSITION_JOB_DONE,
      TRANSITION_JOB_DONE_CUSTOMER,
      TRANSITION_JOB_DONE_RELEASE_FUNDS,
    ],
    instruction: {
      provider: 'TransactionSteps.providerCompleteWork',
      customer: 'TransactionSteps.customerCanReleaseFunds',
    },
    action: {
      provider: {
        label: 'TransactionSteps.markJobDone',
        handler: 'onJobReady',
        variant: 'primary',
      },
      customer: {
        label: 'TransactionSteps.releaseFundsButton',
        handler: 'onJobDoneReleaseFunds',
        variant: 'primary',
      },
    },
    helpText: 'TransactionSteps.workInProgressHelp',
  },
  {
    id: 'work-completed',
    title: 'TransactionSteps.workCompleted',
    description: 'TransactionSteps.workCompletedDesc',
    completedBy: [TRANSITION_RELEASE_FUNDS, TRANSITION_JOB_DONE_RELEASE_FUNDS],
    instruction: {
      customer: 'TransactionSteps.customerReleaseFunds',
      provider: 'TransactionSteps.providerWaitingRelease',
    },
    action: {
      customer: {
        label: 'TransactionSteps.releaseFundsButton',
        handler: 'onReleaseFunds',
        variant: 'primary',
      },
    },
    helpText: 'TransactionSteps.workCompletedHelp',
  },
  {
    id: 'payment-released',
    title: 'TransactionSteps.paymentReleased',
    description: 'TransactionSteps.paymentReleasedDesc',
    completedBy: [TRANSITION_RELEASE_FUNDS, TRANSITION_JOB_DONE_RELEASE_FUNDS],
    instruction: {
      provider: 'TransactionSteps.providerSendInvoiceInstruction',
    },
    helpText: 'TransactionSteps.paymentReleasedHelp',
  },
  {
    id: 'reviews',
    title: 'TransactionSteps.reviews',
    description: 'TransactionSteps.reviewsDesc',
    completedBy: [TRANSITION_REVIEW_2_BY_CUSTOMER, TRANSITION_REVIEW_2_BY_PROVIDER],
    instruction: {
      both: 'TransactionSteps.leaveReviewInstruction',
    },
    action: {
      both: {
        label: 'TransactionSteps.writeReviewButton',
        handler: 'onOpenReviewModal',
        variant: 'secondary',
      },
    },
    helpText: 'TransactionSteps.reviewsHelp',
  },
];

const INVOICE_PROCESS_STEPS = [
  {
    id: 'conversation-started',
    title: 'TransactionSteps.conversationStarted',
    description: 'TransactionSteps.conversationStartedDesc',
    completedBy: [TRANSITION_MAKE_CUSTOM_PRO_OFFER, TRANSITION_MAKE_CUSTOM_OFFER],
    instruction: {
      customer: 'TransactionSteps.customerDiscussDetails',
      provider: 'TransactionSteps.providerMakeOffer',
    },
    helpText: 'TransactionSteps.conversationStartedHelp',
  },
  {
    id: 'offer-sent',
    title: 'TransactionSteps.offerSentWaitingAcceptance',
    description: 'TransactionSteps.offerSentDescPro',
    completedBy: [TRANSITION_ACCEPT_CUSTOM_PRO_OFFER, TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE],
    instruction: {
      customer: 'TransactionSteps.customerReviewProOffer',
      provider: 'TransactionSteps.providerWaitingAcceptance',
    },
    action: {
      customer: {
        label: 'ProcessSteps.viewFullDetails',
        handler: 'onOpenOfferModal',
        variant: 'secondary',
      },
    },
    helpText: 'TransactionSteps.offerSentHelpPro',
  },
  {
    id: 'work-in-progress',
    title: 'TransactionSteps.workInProgress',
    description: 'TransactionSteps.workInProgressDesc',
    completedBy: [
      TRANSITION_JOB_DONE_PRO,
      TRANSITION_JOB_DONE_INVOICE,
      TRANSITION_JOB_DONE_ACCEPT_INVOICE,
      TRANSITION_JOB_DONE_RELEASE_FUNDS,
    ],
    instruction: {
      provider: 'TransactionSteps.providerCompleteWork',
      customer: 'TransactionSteps.customerCanAcceptInvoice',
    },
    action: {
      provider: {
        label: 'TransactionSteps.markJobDone',
        handler: 'onJobDoneInvoicePath',
        variant: 'primary',
      },
      customer: {
        label: 'TransactionSteps.acceptJobButton',
        handler: 'onJobDoneAcceptInvoice',
        variant: 'primary',
      },
    },
    helpText: 'TransactionSteps.workInProgressHelp',
  },
  {
    id: 'ready-to-bill',
    title: 'TransactionSteps.readyToBill',
    description: 'TransactionSteps.readyToBillDesc',
    completedBy: [
      TRANSITION_JOB_ACCEPTED,
      TRANSITION_ACCEPT_JOB_INVOICE,
      TRANSITION_JOB_DONE_ACCEPT_INVOICE,
    ],
    instruction: {
      customer: 'TransactionSteps.customerAcceptJob',
      provider: 'TransactionSteps.providerWaitingJobAcceptance',
    },
    action: {
      customer: {
        label: 'TransactionSteps.acceptJobButton',
        handler: 'onAcceptJobInvoicePath',
        variant: 'primary',
      },
    },
    helpText: 'TransactionSteps.readyToBillHelp',
  },
  {
    id: 'job-accepted',
    title: 'TransactionSteps.jobAccepted',
    description: 'TransactionSteps.jobAcceptedDesc',
    completedBy: [
      TRANSITION_REVIEW_1_BY_CUSTOMER,
      TRANSITION_REVIEW_1_BY_PROVIDER,
      TRANSITION_SKIP_TO_REVIEWS,
    ],
    instruction: {
      provider: 'TransactionSteps.providerSendInvoiceInstruction',
    },
    helpText: 'TransactionSteps.jobAcceptedHelp',
  },
  {
    id: 'reviews',
    title: 'TransactionSteps.reviews',
    description: 'TransactionSteps.reviewsDesc',
    completedBy: [TRANSITION_REVIEW_2_BY_CUSTOMER, TRANSITION_REVIEW_2_BY_PROVIDER],
    instruction: {
      both: 'TransactionSteps.leaveReviewInstruction',
    },
    action: {
      both: {
        label: 'TransactionSteps.writeReviewButton',
        handler: 'onOpenReviewModal',
        variant: 'secondary',
      },
    },
    helpText: 'TransactionSteps.reviewsHelp',
  },
];

// usesInvoicePath: txUsesInvoicePath(tx). isLegacyPro: txIsLegacyProProcess(tx).
// The legacy pro process has no customer shortcut, so its customer waits.
export const getProcessSteps = (usesInvoicePath, isLegacyPro = false) => {
  if (!usesInvoicePath) return DEFAULT_PROCESS_STEPS;
  if (!isLegacyPro) return INVOICE_PROCESS_STEPS;
  return INVOICE_PROCESS_STEPS.map(step =>
    step.id === 'work-in-progress'
      ? {
          ...step,
          instruction: {
            ...step.instruction,
            customer: 'TransactionSteps.customerWaitForProviderToMarkComplete',
          },
          action: { provider: step.action.provider },
        }
      : step
  );
};

/**
 * Get the current step index based on transaction transitions
 */
export const getCurrentStepIndex = (transaction, steps) => {
  const transitionNames = (transaction.attributes.transitions || []).map(t => t.transition);
  const lastTransition = transaction.attributes.lastTransition;

  // After cancel-payment the transaction is back in STATE_ENQUIRY. The request-payment
  // transition is still in history (Sharetribe history is append-only) and would otherwise
  // advance the panel to the "offer-sent" step, which misrepresents the actual state.
  if (lastTransition === TRANSITION_CANCEL_PAYMENT) {
    return 0;
  }

  // Special handling for review steps
  // After payment is released or job is accepted, payment-released step is completed
  // and we move to the reviews step which remains current until BOTH reviews are completed
  const reviewStartTransitions = [
    TRANSITION_RELEASE_FUNDS,
    TRANSITION_JOB_DONE_RELEASE_FUNDS,
    TRANSITION_JOB_ACCEPTED,
    TRANSITION_SKIP_TO_REVIEWS,
    TRANSITION_ACCEPT_JOB_INVOICE,
    TRANSITION_JOB_DONE_ACCEPT_INVOICE,
  ];

  const firstReviewTransitions = [TRANSITION_REVIEW_1_BY_CUSTOMER, TRANSITION_REVIEW_1_BY_PROVIDER];

  const secondReviewTransitions = [
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
  ];

  // If we've started reviews but not completed both, find the reviews step
  const hasStartedReviews =
    reviewStartTransitions.some(t => transitionNames.includes(t)) ||
    firstReviewTransitions.some(t => transitionNames.includes(t));

  const hasBothReviews = secondReviewTransitions.some(t => transitionNames.includes(t));

  if (hasStartedReviews && !hasBothReviews) {
    // Find the reviews step - this is now the LAST step in all processes
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (step.id === 'reviews') {
        return i; // Stay on reviews step until both reviews are done
      }
    }
  }

  // Find the last completed step
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i];
    if (step.completedBy?.some(t => transitionNames.includes(t))) {
      return i + 1; // Return next step as current
    }
  }

  return 0; // First step is current
};
